import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../networking/axiosconfig';
import IdentityCardFormat from './IdentityCardFormat';
import { Download } from 'lucide-react';
import { toast } from 'react-hot-toast';
import html2canvas from 'html2canvas';

const IdentityCardView = () => {
    const { userId } = useParams();
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    setError("Authentication required");
                    setLoading(false);
                    return;
                }

                const response = await axiosInstance.get(`/api/users/${userId}`, {
                    headers: { Authorization: `Bearer ${token.trim()}` }
                });

                if (response.data?.status && response.data?.data) {
                    setUserData(response.data.data);
                } else {
                    setError("Failed to fetch user data for the given user ID");
                }
            } catch (err) {
                console.error('Error fetching user data:', err);
                setError("Error fetching user data from server");
            } finally {
                setLoading(false);
            }
        };

        if (userId) {
            fetchUserData();
        } else {
            setError("No User ID provided in URL");
            setLoading(false);
        }
    }, [userId]);

    if (loading) {
        return <div className="flex justify-center items-center min-h-screen">Loading ID Card Data...</div>;
    }

    if (error || !userData) {
        return (
            <div className="flex justify-center items-center min-h-screen text-red-500 font-semibold text-lg">
                {error || "User data not found"}
            </div>
        );
    }

    const handleDownloadIDCard = async () => {
        try {
            const element = document.getElementById('identity-card-wrapper');
            if (!element) {
                toast.error('Failed to locate ID card element');
                return;
            }

            toast.loading('Downloading ID Card...', { id: 'download-id' });

            // Ensure images are fully loaded and use high scale for crisp resolution
            const canvas = await html2canvas(element, {
                scale: 3,
                useCORS: true,
                logging: false,
                backgroundColor: null // Ensures transparent background properly captures rounded corners & shadow margins
            });

            // Create a temporary link and trigger download for the PNG
            const link = document.createElement('a');
            link.download = `ID_Card_${userData.first_name || 'User'}_${userData.last_name || ''}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();

            toast.success('ID Card Image downloaded successfully!', { id: 'download-id' });
        } catch (err) {
            console.error('Error downloading ID card:', err);
            toast.error('Failed to download ID card details', { id: 'download-id' });
        }
    };

    return (
        <div className="flex flex-col items-center min-h-screen bg-gray-100 py-10 w-full relative">
            <div className="mb-6 text-center">
                <h2 className="text-2xl font-bold text-gray-800">Identity Card Preview</h2>
                <p className="text-gray-500 mt-1">Viewing ID Card for Employee {userData.employeeId || userId}</p>
                <button
                    onClick={handleDownloadIDCard}
                    className="mt-4 flex items-center justify-center gap-2 px-6 py-2.5 mx-auto bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-md active:bg-blue-800"
                >
                    <Download size={18} />
                    Download ID Card
                </button>
            </div>

            {/* Padded wrapper to allow html2canvas to capture the box shadow and transparent rounded corners perfectly */}
            <div id="identity-card-wrapper" className="p-8 pb-10" style={{ background: 'transparent' }}>
                <IdentityCardFormat userData={userData} />
            </div>
        </div>
    );
};

export default IdentityCardView;
